import type { BotStageApi } from "@/Modules/Models/Api/FlexoBot";
import type { MessageTypeWebSocket } from "./Message";
import type { UserApi, TranscriptionApi } from "@/Modules/Models/Api/User";
import type { UserStatus } from "@/Common/Models/Domain/UserStatus";
import type { BreakApi } from "@/Modules/Models/Api/Breaks";
import type { VIDEO_CALL_COMPLETE_REASON } from "@/Modules/Models/Api/VideoCalls";

export enum UserStatusWebSocket {
  OFFLINE = "offline",
  ONLINE = "online",
  COMPOSING = "composing",
  IDLE = "idle",
}

export interface WebSocketMessage {
  isNew?: true;
  chat: string;
}

export interface ChatAuthMessageSocket {
  caps: number;
  event_list_size: number;
  nonce: string;
  signature: string;
  time: number;
  type: MessageTypeWebSocket.AUTH;
  user: string;
  fast?: boolean;
}

export interface ChatGetEventMessageSocket extends WebSocketMessage {
  event_list_size: number;
  nonce: string;
  type: MessageTypeWebSocket.GET_CHAT_EVENTS;
  oldest_eid: string;
}

export interface ChatSendTextEventMessageSocket extends WebSocketMessage {
  action: string;
  created: number;
  etype: ChatRoomItemTypeMessageWebSocket.TEXT;
  extra: string;
  nonce: string;
  private: false;
  text: string;
  type: MessageTypeWebSocket.CHAT_ROOM_ITEM;
}

export interface ChatSendFileEventMessageSocket extends WebSocketMessage {
  action: string;
  content_type: string;
  created: number;
  extra: string;
  nonce: string;
  private: false;
  size: number;
  upload_id: string;
  type: MessageTypeWebSocket.CHAT_ROOM_ITEM;
  etype: ChatRoomItemTypeMessageWebSocket;
}

export interface ChatSendDocumentEventMessageSocket extends ChatSendFileEventMessageSocket {
  etype: ChatRoomItemTypeMessageWebSocket.DOCUMENT;
  file_name: string;
}

export interface ChatSendImageEventMessageSocket extends ChatSendFileEventMessageSocket {
  etype: ChatRoomItemTypeMessageWebSocket.IMAGE;
  height: number;
  width: number;
  thumbnail: string;
}

export interface ChatSendAudioEventMessageSocket extends ChatSendFileEventMessageSocket {
  etype: ChatRoomItemTypeMessageWebSocket.AUDIO;
  length: number;
}

export enum TYPING_STATUS {
  COMPOSING = "composing",
  IDLE = "idle",
}

export interface TypingStatusMessageWebSocket extends WebSocketMessage {
  nonce: string;
  status: TYPING_STATUS;
  type: MessageTypeWebSocket.STATUS;
}

export interface ConfirmEventMessageWebSocket extends WebSocketMessage {
  etype: ChatRoomItemTypeMessageWebSocket.CONFIRMATION;
  nonce: string;
  rd_eid: string;
  type: MessageTypeWebSocket.CHAT_ROOM_ITEM;
}

export interface ReceivedEventMessageWebSocket extends WebSocketMessage {
  etype: ChatRoomItemTypeMessageWebSocket.CONFIRMATION;
  nonce: string;
  rd_eid: string;
  eid: string;
  user: string;
  type: MessageTypeWebSocket.CHAT_ROOM_ITEM;
}

export interface TextChatRoomItemMessageWebSocket extends ChatRoomItemMessageWebSocket {
  text: string;
}

export interface ResultMessageConfirmationWebSocket {
  created: number;
  eid: string;
  nonce: string; // "chat:message:${chatId}:${message position in chat}"|| "${timestamp}";
  type: MessageTypeWebSocket.RESULT;
  url?: string; // File url
}
export interface EventMessageConfirmationWebSocket {
  created: number;
  chat: string;
  eid: string;
  rd_eids: string[];
  rd_eid: string; // eid of the message that is being confirmed
  type: MessageTypeWebSocket.CHAT_ROOM_ITEM;
  isReceived: boolean;
  etype: ChatRoomItemTypeMessageWebSocket.CONFIRMATION;
}

export enum CustomChatRoomItemTypeMessageWebSocket {
  SUBSCRIPTION_REQUEST = "subscription_request",
  SUBSCRIPTION_RENEWED = "subscription_renewed",
  SUBSCRIPTION_EXPIRED = "subscription_expired",
  SUBSCRIPTION_CANCELLED = "subscription_cancelled",
  SUBSCRIPTION_CHANGED = "subscription_changed",
  PAYMENT_OK = "payment_ok",
  PAYMENT_ERROR = "payment_error",
  SHOW_PLANS = "show_plans",
  PATIENT_COME_BACK = "patient_come_back",
  PATIENT_SSO = "patient_sso",
  REASSIGN_REQUEST_COMPLETED = "reassign_request_completed",
  QUESTIONNAIRE_ANSWERED = "questionnaire_answered",
  VIDEO_CALL_ENDED = "video_call_ended",
  VIDEO_CALL_REJECTED = "video_call_rejected",
  VIDEO_CALL_MISSED = "video_call_missed",
  CONTRACT_SIGNED = "contract_signed",
  NEW_CONTRACT = "new_contract",
  BREAK_CREATED = "break_created",
  BREAK_DELETED = "break_deleted",
  BREAK_START = "break_start",
  BREAK_END = "break_end",
  SCHEDULE_CREATED = "schedule_created",
  SCHEDULE_CANCELLED = "schedule_cancelled",
  NEW_ACTIVITY_CENTER_EVENT = "new_activity_center_event",
  UPDATED_ACTIVITY_CENTER_EVENT = "updated_activity_center_event",
  CLINIC_DISCHARGE = "clinic_discharge",
  PATIENT_REASSIGN = "patient_reassign",
}

export interface CustomChatRoomItemMessageWebSocket extends ChatRoomItemMessageWebSocket {
  action: CustomChatRoomItemTypeMessageWebSocket;
  extra: string | number;
}

export interface ChatUserAddedEventMessageSocket extends WebSocketMessage {
  created: number;
  eid: string;
  etype: ChatRoomItemTypeMessageWebSocket.USER_ADDED;
  is_received: boolean;
  profile: string;
  user: string;
  old: boolean;
}

export interface ChatUserLeaveEventMessageSocket extends WebSocketMessage {
  created: number;
  eid: string;
  etype: ChatRoomItemTypeMessageWebSocket.USER_LEAVE;
  is_received: boolean;
  profile: string;
  user: string;
  old: boolean;
}

export interface DocumentChatRoomItemMessageWebSocket extends WebSocketMessage {
  content_type: string;
  created: number;
  eid: string;
  etype: 16;
  file_name: string;
  old: boolean;
  is_received: boolean;
  size: number;
  url: string;
  user: string;
}

export interface ImageChatItemMessageWebSocket extends WebSocketMessage {
  extra: string;
  created: number;
  eid: string;
  etype: 2;
  height: number;
  is_received: boolean;
  size: number;
  thumbnail: string;
  url: string;
  user: string;
  width: number;
  old: boolean;
}

export interface AudioChatItemMessageWebsocket extends WebSocketMessage {
  created: number;
  eid: string;
  etype: 14;
  is_received: boolean;
  length: number;
  size: number;
  url: string;
  user: string;
  old: boolean;
}

export interface BotStageChangedMessageWebSocket {
  type: MessageTypeWebSocket.BOT_STAGE_CHANGED;
  data: BotStageApi;
}

export interface UserChangedMessageWebSocket {
  type: MessageTypeWebSocket.USER_CHANGED;
  data: UserApi;
}

export interface ChatRoomWebSocket extends WebSocketMessage {
  created: Date;
  ctype: number;
  ietag: string;
  last_event?: ChatRoomItemMessageWebSocket;
  part: ChatRoomParticipantWebSocket[];
  role: number; // rol del usuario logado --> enum
  state: UserStatus;
  total_unread_events: number;
  left?: boolean; // true if a user left the chat, undefined in other cases.
  current_break: BreakApi | null;
  is_ai_enabled: boolean;
  transcription: TranscriptionApi;
}

export interface ChatStateWebSocket extends ChatRoomWebSocket {
  type: MessageTypeWebSocket.CHAT;
}

export enum CHAT_ROLE {
  PATIENT = 0,
  THERAPIST = 1,
  COACH = 2,
}

export interface ChatRoomParticipantWebSocket {
  caps: number;
  ietag: string;
  image: string | null;
  is_b2b: boolean;
  is_couple: boolean;
  is_under_18: boolean;
  is_promo: boolean;
  left: boolean;
  name: string;
  role: CHAT_ROLE;
  state: null | UserStatus;
  user: string;
  customer_uuid?: string;
}

export interface ChatRoomListMessageWebSocket {
  chats: ChatRoomWebSocket[];
  type: MessageTypeWebSocket.CHAT_ROOM_LIST;
}

export enum VIDEO_CALL_STATUS {
  IN_PROGRESS = "in-progress",
}

export interface VideoCallDataPayload {
  room_sid: string;
  room_token: string;
  room_uuid: string;
  is_transcription_enabled: boolean;
  is_rating_required: boolean;
  completed_reason: VIDEO_CALL_COMPLETE_REASON;
}

export interface VideoCallRoomCreatedMessageWebSocket {
  type: MessageTypeWebSocket.VIDEO_CALL_ROOM_CREATED;
  data: VideoCallDataPayload;
}

export interface VideoCallRoomCompletedMessageWebSocket {
  type: MessageTypeWebSocket.VIDEO_CALL_ROOM_COMPLETED;
  data: VideoCallDataPayload;
}

export enum ChatRoomItemTypeMessageWebSocket {
  TEXT = 0,
  CONFIRMATION = 1,
  CUSTOM = 15,
  IMAGE = 2,
  VIDEO = 3,
  USER_ADDED = 9,
  USER_LEAVE = 10,
  AUDIO = 14,
  DOCUMENT = 16,
}

export interface ChatRoomItemMessageWebSocket extends WebSocketMessage {
  created: number;
  eid: string;
  etype: ChatRoomItemTypeMessageWebSocket;
  user: string;
  old?: boolean;
  rd_eid?: string;
  is_received: boolean;
}

export interface ChatRoomItemListMessageWebSocket {
  events: unknown[];
  last: boolean;
  type: MessageTypeWebSocket.CHAT_ROOM_ITEM_LIST;
  nonce: string;
}

export interface UserStatusMessageWebSocket {
  user: string;
  type: MessageTypeWebSocket.STATUS;
  status: UserStatusWebSocket;
  active: number;
  chat?: string;
}

export interface UserStatusChangeMessageWebSocket {
  user: UserApi;
  status: UserStatus;
  type: MessageTypeWebSocket.USER_STATUS_CHANGE;
}

export interface ChatAuthMessageSocket {
  caps: number;
  event_list_size: number;
  nonce: string;
  signature: string;
  time: number;
  type: MessageTypeWebSocket.AUTH;
  user: string;
}
